import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '../lib/auth';
import {
  Search, Phone, MoreVertical, Paperclip, Send, ShieldAlert, MessageSquare,
} from 'lucide-react';
import { TID, CHAT_SECURITY_WARNING, type MessageDto } from '@yalidavet/shared';
import { api } from '@/lib/api';
import { cn, chatDateLabel, formatTimeTr, relativeTimeTr, initials } from '@/lib/utils';

export default function MessagesPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useUser();
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [optionsOpen, setOptionsOpen] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  /* ------------ Conversations (polled for near-real-time) ------------ */
  const { data: conversations, isLoading: loadingConvs } = useQuery({
    queryKey: ['conversations', search],
    queryFn: () => api.conversations(search || undefined),
    refetchInterval: 15_000,
  });

  const active = useMemo(
    () => conversations?.items.find((c) => c.id === conversationId) ?? null,
    [conversations, conversationId],
  );

  /* ------------ Messages (polled while the thread is open) ---------- */
  const { data: thread } = useQuery({
    queryKey: ['messages', conversationId],
    queryFn: () => api.messages(conversationId!),
    enabled: !!conversationId,
    refetchInterval: 5_000,
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread?.items.length, conversationId]);

  const send = useMutation({
    mutationFn: (payload: { body: string; attachmentR2Key?: string; attachmentName?: string; messageType?: string }) =>
      api.sendMessage(conversationId!, payload),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['messages', conversationId] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
      qc.invalidateQueries({ queryKey: ['unread-count'] });
    },
  });

  const attach = useMutation({
    mutationFn: async (file: File) => {
      const up = await api.uploadChatAttachment(file);
      return api.sendMessage(conversationId!, {
        body: '',
        messageType: up.type.startsWith('image/') ? 'image' : 'file',
        attachmentR2Key: up.key,
        attachmentName: up.name,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messages', conversationId] }),
  });

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !conversationId) return;
    send.mutate({ body });
  };

  /* -------- Group messages by day for the date dividers ------------ */
  const grouped = useMemo(() => groupByDay(thread?.items ?? []), [thread?.items]);

  return (
    <div className="yd-container grid h-[calc(100vh-4rem)] gap-0 py-4 md:grid-cols-[340px_1fr]">
      {/* ==================== Sidebar ==================== */}
      <aside
        className={cn(
          'flex flex-col overflow-hidden rounded-l-xl2 border border-ink-100 bg-white',
          conversationId && 'hidden md:flex',
        )}
        data-testid={TID.chatSidebar}
      >
        <div className="border-b border-ink-100 p-3">
          <h1 className="mb-3 font-display text-lg font-bold text-ink-900">Mesajlar</h1>
          <label className="relative block">
            <Search className="absolute left-3 top-3 h-4 w-4 text-ink-400" aria-hidden />
            <span className="sr-only">Sohbet ara</span>
            <input
              className="yd-input pl-9"
              placeholder="Sohbet ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid={TID.chatSearch}
            />
          </label>
        </div>

        <ul className="flex-1 overflow-y-auto" data-testid="chat-conversation-list">
          {loadingConvs && <li className="p-4"><div className="yd-skeleton h-14" /></li>}

          {conversations?.items.length === 0 && (
            <li className="p-8 text-center text-sm text-ink-500" data-testid="chat-empty">
              Henüz mesajınız yok.
            </li>
          )}

          {conversations?.items.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => navigate(`/mesajlar/${c.id}`)}
                className={cn(
                  'flex w-full items-start gap-3 border-b border-ink-50 p-3 text-left transition-colors hover:bg-ink-50',
                  c.id === conversationId && 'bg-bosphorus-50',
                )}
                data-testid={TID.chatConversation(c.id)}
                data-unread={c.unreadCount}
              >
                {c.counterpart.avatarUrl ? (
                  <img src={c.counterpart.avatarUrl} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" />
                ) : (
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-bosphorus-100 text-sm font-semibold text-bosphorus-800">
                    {initials(c.counterpart.fullName)}
                  </span>
                )}

                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="truncate font-semibold text-ink-900">{c.counterpart.fullName}</span>
                    <span className="shrink-0 text-[11px] text-ink-400">{relativeTimeTr(c.lastMessageAt)}</span>
                  </span>
                  {c.mansionTitle && (
                    <span className="block truncate text-[11px] text-bosphorus-700">{c.mansionTitle}</span>
                  )}
                  <span className="mt-0.5 flex items-center justify-between gap-2">
                    <span className="truncate text-sm text-ink-500">{c.lastMessagePreview ?? 'Yeni sohbet'}</span>
                    {c.unreadCount > 0 && (
                      <span
                        className="grid h-5 min-w-5 shrink-0 place-items-center rounded-full bg-bosphorus-600 px-1.5 text-[11px] font-bold text-white"
                        data-testid={TID.chatUnreadBadge}
                      >
                        {c.unreadCount}
                      </span>
                    )}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      {/* ==================== Chat window ==================== */}
      <section
        className={cn(
          'flex flex-col overflow-hidden rounded-r-xl2 border border-l-0 border-ink-100 bg-white',
          !conversationId && 'hidden md:flex',
        )}
        data-testid={TID.chatWindow}
      >
        {!active ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-ink-400">
            <MessageSquare className="h-10 w-10" aria-hidden />
            <p className="text-sm">Bir sohbet seçin</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <header className="flex items-center justify-between gap-3 border-b border-ink-100 p-3" data-testid={TID.chatHeader}>
              <div className="flex min-w-0 items-center gap-3">
                {active.counterpart.avatarUrl ? (
                  <img src={active.counterpart.avatarUrl} alt="" className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-bosphorus-100 text-sm font-semibold text-bosphorus-800">
                    {initials(active.counterpart.fullName)}
                  </span>
                )}
                <div className="min-w-0">
                  <p className="truncate font-semibold text-ink-900">{active.counterpart.fullName}</p>
                  {active.mansionTitle && (
                    <p className="truncate text-xs text-ink-500" data-testid="chat-property-context">
                      {active.mansionTitle}
                    </p>
                  )}
                </div>
              </div>

              <div className="relative flex shrink-0 items-center gap-1">
                <button type="button" className="yd-btn-ghost p-2" aria-label="Sesli arama" data-testid={TID.chatCall}>
                  <Phone className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  className="yd-btn-ghost p-2"
                  aria-label="Seçenekler"
                  aria-expanded={optionsOpen}
                  onClick={() => setOptionsOpen((v) => !v)}
                  data-testid={TID.chatOptions}
                >
                  <MoreVertical className="h-5 w-5" />
                </button>
                {optionsOpen && (
                  <ul className="absolute right-0 top-11 z-20 w-48 overflow-hidden rounded-lg border border-ink-100 bg-white py-1 shadow-widget" data-testid="chat-options-menu">
                    {active.mansionId && (
                      <li>
                        <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-ink-50" onClick={() => navigate(`/mulk/${active.mansionId}`)}>
                          İlanı görüntüle
                        </button>
                      </li>
                    )}
                    <li><button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-ink-50">Sohbeti arşivle</button></li>
                    <li><button type="button" className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50">Kullanıcıyı şikayet et</button></li>
                  </ul>
                )}
              </div>
            </header>

            {/* Security banner */}
            <p
              className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-900"
              data-testid={TID.chatSecurityBanner}
            >
              <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
              {CHAT_SECURITY_WARNING}
            </p>

            {/* Messages */}
            <div className="flex-1 space-y-4 overflow-y-auto bg-ink-50/40 p-4">
              {grouped.map(({ day, messages }) => (
                <div key={day} className="space-y-2">
                  <p className="sticky top-0 z-10 text-center" data-testid={TID.chatDateDivider}>
                    <span className="rounded-full bg-white px-3 py-1 text-[11px] font-medium text-ink-500 shadow-sm">
                      {day}
                    </span>
                  </p>

                  {messages.map((m) => {
                    const mine = m.senderId !== active.counterpart.id;
                    return (
                      <div
                        key={m.id}
                        className={cn('flex', mine ? 'justify-end' : 'justify-start')}
                        data-testid={TID.chatMessage}
                        data-own={mine}
                      >
                        <div
                          className={cn(
                            'max-w-[75%] rounded-2xl px-3.5 py-2 text-sm shadow-sm',
                            mine ? 'rounded-br-sm bg-bosphorus-600 text-white' : 'rounded-bl-sm bg-white text-ink-800',
                          )}
                        >
                          {m.attachmentUrl && m.messageType === 'image' && (
                            <img src={m.attachmentUrl} alt={m.attachmentName ?? ''} className="mb-1 max-h-60 rounded-lg" />
                          )}
                          {m.attachmentUrl && m.messageType === 'file' && (
                            <a href={m.attachmentUrl} target="_blank" rel="noreferrer" className="mb-1 flex items-center gap-1.5 underline">
                              <Paperclip className="h-3.5 w-3.5" /> {m.attachmentName}
                            </a>
                          )}
                          {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
                          <p className={cn('mt-0.5 text-right text-[10px]', mine ? 'text-bosphorus-100' : 'text-ink-400')}>
                            {formatTimeTr(m.createdAt)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSend} className="flex items-center gap-2 border-t border-ink-100 p-3">
              <input
                ref={fileRef}
                type="file"
                className="sr-only"
                onChange={(e) => e.target.files?.[0] && attach.mutate(e.target.files[0]!)}
                data-testid="chat-file-input"
              />
              <button
                type="button"
                className="yd-btn-ghost shrink-0 p-2"
                onClick={() => fileRef.current?.click()}
                aria-label="Dosya ekle"
                data-testid={TID.chatAttach}
              >
                <Paperclip className="h-5 w-5" />
              </button>

              <input
                className="yd-input"
                placeholder="Bir mesaj yazın..."
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                data-testid={TID.chatInput}
              />

              <button
                type="submit"
                className="yd-btn-primary shrink-0 px-3"
                disabled={!draft.trim() || send.isPending}
                aria-label="Gönder"
                data-testid={TID.chatSend}
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}

function groupByDay(messages: MessageDto[]) {
  const map = new Map<string, MessageDto[]>();
  for (const m of messages) {
    const key = chatDateLabel(m.createdAt);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(m);
  }
  return Array.from(map, ([day, msgs]) => ({ day, messages: msgs }));
}
